// Generic widgets (generic_text, generic_number, generic_full_title, ...) share ONE
// field_id across many widgets. Each widget is an independent input keyed by `name`.
// Regular fields must never be affected by anything in this file.

const GENERIC_TABLE_FIELD_ID = "generic_table"; // handled by its own renderer
const TITLE_TYPES = new Set(["full_title", "full_subTitle"]);

// Mirrors GENERIC_FORM_FIELDS (GenericFormFieldPanel). Keys are lowercase.
// When you add a generic type there, add it here too.
const GENERIC_FIELD_REGISTRY = {
  generic_text: { type: "text", label: "Text" },
  generic_number: { type: "number", label: "Number" },
  generic_dollar: { type: "dollar", label: "Dollar" },
  generic_date: { type: "date", label: "Date" },
  generic_multiline: { type: "multiline", label: "Multiline" },
  generic_email: { type: "email", label: "Email" },
  generic_phone: { type: "mobile", label: "Phone" },
  generic_money: { type: "money", label: "Money" },
  generic_todaydate: { type: "todayDate", label: "Today Date" },
  generic_full_title: { type: "full_title", label: "Full Title" },
  generic_full_subtitle: { type: "full_subTitle", label: "Full Sub Title" },
  generic_total: { type: "total", label: "Total" },
};

const getFieldId = (field) =>
  String(field?.field_id ?? "")
    .trim()
    .toLowerCase();

export const isGenericFormField = (field) => {
  const fieldId = getFieldId(field);

  if (!fieldId || fieldId === GENERIC_TABLE_FIELD_ID) return false;

  return Boolean(field?.is_generic) || fieldId.startsWith("generic_");
};

export const isGenericTitleField = (field) =>
  isGenericFormField(field) &&
  (TITLE_TYPES.has(field?.type) || TITLE_TYPES.has(field?.field_type));

// The field_id is the authoritative tag for a generic widget, so its semantic type
// (mobile, dollar, number, ...) is resolved from the registry. Unknown generic ids
// keep whatever field_type the widget already has.
// Same input object always returns the same output object.
const normalizedFieldCache = new WeakMap();

export const normalizeGenericFieldType = (field) => {
  if (!field || typeof field !== "object" || !isGenericFormField(field)) {
    return field;
  }

  if (normalizedFieldCache.has(field)) return normalizedFieldCache.get(field);

  const registryEntry = GENERIC_FIELD_REGISTRY[getFieldId(field)];

  // A registered generic id is authoritative. The widget's own type may be a PDF
  // type or an already-defaulted "text", so it must not decide the behaviour.
  const semanticType = field.text_connection_source
    ? "multiline"
    : (registryEntry?.type ?? field.field_type);

  const needsFieldType =
    Boolean(semanticType) && field.field_type !== semanticType;
  const needsType =
    Boolean(semanticType) &&
    field.type !== semanticType &&
    (Boolean(registryEntry) || !field.type || field.type === "PDFTextField");

  const label = field.label || field.field_label || registryEntry?.label;
  const needsLabel = !field.label && Boolean(label);
  const needsFieldLabel = !field.field_label && !field.label && Boolean(label);

  const normalized =
    needsFieldType || needsType || needsLabel || needsFieldLabel
      ? {
          ...field,
          ...(needsFieldType ? { field_type: semanticType } : {}),
          ...(needsType ? { type: semanticType } : {}),
          ...(needsLabel ? { label } : {}),
          ...(needsFieldLabel ? { field_label: label } : {}),
        }
      : field;

  normalizedFieldCache.set(field, normalized);
  return normalized;
};

export const normalizeGenericFields = (fields) =>
  Array.isArray(fields) ? fields.map(normalizeGenericFieldType) : fields;

// Wraps an existing field_id deduper (e.g. dedupeFieldsByCanonicalId) so generic
// widgets are always kept. With no generics present it calls `dedupe` exactly as before.
export const dedupeKeepingGenericFields = (fields = [], dedupe) => {
  const regularFields = fields.filter((field) => !isGenericFormField(field));
  if (regularFields.length === fields.length) return dedupe(fields);

  const kept = dedupe(regularFields);
  const keptRefs = new Set(kept);
  const keptNames = new Set(kept.map((field) => field?.name).filter(Boolean));

  return fields.filter(
    (field) =>
      isGenericFormField(field) ||
      keptRefs.has(field) ||
      (field?.name && keptNames.has(field.name)),
  );
};
